import {
	AccountActivationFormData,
	ActivationResponse,
	ActivationUserData,
	EAccountActivationEmptyFields,
} from "@data-types";
import { localizationService, history } from "@state";
import { getCurrentCulture, isNullOrWhitespace } from "@utils";
import { getRedirectOriginUrl } from "@utils/specific/capacitor/helpers";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, observable, runInAction } from "mobx";
import { PlayerPromoSendingModeEnum } from "@api-types/user/AccountActivationRequestDto";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { UserAuthStore } from "@state/shared";
import { UserData } from "./types";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { LoaderStore } from "@state/stores/common";
import { EmailChangeService } from "@services/account-settings/EmailChangeService";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const  AccountActivationService = lazy(loadFailPath, ()=>import("@services/membership/account-activation/AccountActivationService"));

export class AccountActivationStore {
	userAuthStore: UserAuthStore;
	loaderStore: LoaderStore;

	@observable public isAccountActivationWorking: boolean = false;
	@observable public accountActivationData?: ActivationResponse | null = null;
	@observable public emptyFields: EAccountActivationEmptyFields =
		EAccountActivationEmptyFields.NONE;
	@observable public hasAccountActivationError: boolean = false;
	@observable public isAccountActivationCongratulationsActive: boolean =
		false;

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	public async onInitializeAccountActivation(): Promise<void> {
		if (this.userAuthStore.accountActivation == null) return;

		try {
			this.loaderStore.suspend();

			const response = await (
				await AccountActivationService
			).default.getAccountActivationData(
				this.userAuthStore.accountActivation?.activationToken,
				this.userAuthStore.accountActivation?.agencyId,
				this.userAuthStore.accountActivation?.userName
			);

			if (response.result != null) {
				runInAction(
					() => (this.accountActivationData = response.result)
				);
			} else if (response?.error != null) {
				console.error(response.error);
				runInAction(() => (this.hasAccountActivationError = true));
			}
		} catch (error) {
			console.log(error);

			this.userAuthStore.rootStore.notificationStore.error(
				localizationService.t("GENERIC_ERROR")
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async onAccountActivationSubmit(
		data: AccountActivationFormData
	): Promise<void> {
		if (this.userAuthStore.accountActivation == null) {
			return;
		}

		try {
			this.loaderStore.suspend();

			this.emptyFields = EAccountActivationEmptyFields.NONE;

			const activationModel = {
				activationToken:
					this.userAuthStore.accountActivation.activationToken,
				agencyId: this.userAuthStore.accountActivation.agencyId,
				agencyKey: this.userAuthStore.accountActivation.agencyKey,
				shopId: this.userAuthStore.accountActivation.shopId,
				email: data.email,
				emailChangeUrl: getRedirectOriginUrl(
					`email-activation?mailkey={mailkey}&username={username}`
				),
				password: data.password,
				username: data.userCardNumber,
				depositLimit: Number(data.depositLimit),
				playerPromoSendingMode: data.isReceivingPromoEmailsEnabled
					? PlayerPromoSendingModeEnum.enabled
					: PlayerPromoSendingModeEnum.disabled,
			};

			const response = await (
				await AccountActivationService
			).default.createAccountActivation(activationModel);

			if (response.result != null) {
				this.userAuthStore.loginStore.resolveUserActivationLogin(
					response.result.primary,
					response.result.secondary,
					Number(response.result.errorCode)
				);

				runInAction(() => {
					this.isAccountActivationCongratulationsActive = true;
				});
			} else if (response.error != null) {
				const error = response.error;
				const notifyError =
					this.userAuthStore.rootStore.notificationStore.error;

				if (error?.userData) {
					if (error.userData?.activationToken) {
						//show popup with user data
						this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
							{
								...error.userData,
								isInternetUser: false,
							} as any
						);
						this.setUserConfirmationData(error.userData);
						this.userAuthStore.loginStore.setUserDataConfirmationState(
							true
						);
					} else {
						if (error.userData.shopInfo) {
							//show message about shop
							notifyError(
								localizationService.t(
									"USER.POPUPS.DATA_CONFIRMATION.USER_DATA_CONFIRMATION_REQUIRED",
									{
										0: error.userData.shopInfo,
									}
								)
							);
						} else {
							notifyError(
								localizationService.t(
									"MEMBERSHIP.ACCOUNT_ACTIVATION.ERROR_HAS_OCCURED"
								)
							);
						}
					}
				} else {
					notifyError(
						localizationService.t(
							`MEMBERSHIP.ACCOUNT_ACTIVATION.${error?.message}`
						)
					);
				}
			}
		} catch (error) {
			console.log(error);

			this.userAuthStore.rootStore.notificationStore.error(
				localizationService.t("error-handling:GENERIC_ERROR")
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async onAccountActivationFormSubmit(
		data: AccountActivationFormData
	): Promise<void> {
		if (
			!this.accountActivationData?.userInfo?.isOfflineUserMailApproved &&
			isNullOrWhitespace(data.email) &&
			!this.accountActivationData?.companyInfo
				.isOfflineUserPasswordChangeMandatory &&
			isNullOrWhitespace(data.password)
		) {
			this.emptyFields = EAccountActivationEmptyFields.MAIL_AND_PASSWORD;
		} else if (
			!this.accountActivationData?.userInfo?.isOfflineUserMailApproved &&
			isNullOrWhitespace(data.email)
		) {
			this.emptyFields = EAccountActivationEmptyFields.MAIL;
		} else if (
			!this.accountActivationData?.companyInfo
				.isOfflineUserPasswordChangeMandatory &&
			isNullOrWhitespace(data.password)
		) {
			this.emptyFields = EAccountActivationEmptyFields.PASSWORD;
		} else {
			await this.onAccountActivationSubmit(data);
		}
	}

	@action.bound
	public async onCancelAccountActivation(): Promise<void> {
		this.loaderStore.suspend();

		this.userAuthStore.logoutUser();

		runInAction(() => {
			this.loaderStore.resume();
		});
	}

	@action.bound
	public clearHasAccountActivationError(): void {
		this.hasAccountActivationError = false;
	}

	@action.bound
	public closeEmptyFieldsPopup(): void {
		this.emptyFields = EAccountActivationEmptyFields.NONE;
	}

	@action.bound
	public closeAccountActivationCongratulations(): void {
		this.isAccountActivationCongratulationsActive = false;
	}

	@action.bound
	public setUserConfirmationData(
		userData: ActivationUserData | UserData | null
	) {
		if (userData != null) {
			this.userAuthStore.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.CONFIRMATION_DATA,
				JSON.stringify(userData)
			);
		} else {
			this.userAuthStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.CONFIRMATION_DATA
			);
		}
	}

	@action.bound
	setConfirmationDataState() {
		this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
			JSON.parse(
				this.userAuthStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.CONFIRMATION_DATA
				) as string
			) as UserData
		);
	}

	@action.bound
	handleErrorResponse(clearAccountActivationError: () => void) {
		this.userAuthStore.removeUserFromLocalStorage();
		history.push(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
		clearAccountActivationError();
	}

	@action.bound
	public onDisposeAccountActivation(): void {
		this.accountActivationData = null;
		this.emptyFields = EAccountActivationEmptyFields.NONE;
		this.clearHasAccountActivationError();
	}

	@action.bound
	public onDisposeAccountActivationCongratulations(): void {
		this.isAccountActivationCongratulationsActive = false;
	}

	public async checkForEmailAvailiability(value: string) {
		return await EmailChangeService.checkIsEmailAvailable(value);
	}
}
